/**
 * Bridal configurator URL params — safe parsing (productMeta values may contain commas).
 */
(function () {
  const PRODUCT_META_KEYS = [
    'bodice_mesh',
    'bodice',
    'back',
    'skirt',
    'sleeves',
    'straps',
    'accessories',
  ];

  const PRICE_PARAM_KEYS = ['price', 'customPrice', 'custom_price'];
  const CONFIGURATOR_PARAM_KEYS = ['productMeta', 'product_meta', 'properties', ...PRICE_PARAM_KEYS];

  /** @param {string} raw */
  function decodeParam(raw) {
    if (raw == null || raw === '') return '';
    try {
      return decodeURIComponent(String(raw)).trim();
    } catch {
      return String(raw).trim();
    }
  }

  /**
   * @param {string} raw
   * @returns {Record<string, string>}
   */
  function parseProductMeta(raw) {
    const out = /** @type {Record<string, string>} */ ({});
    const s = decodeParam(raw);
    if (!s) return out;

    /** @type {{ key: string, valueStart: number, keyStart: number }[]} */
    const markers = [];
    for (const key of PRODUCT_META_KEYS) {
      let searchFrom = 0;
      const re = new RegExp(`(^|[,&])${key}=`, 'gi');
      while (searchFrom < s.length) {
        const slice = s.slice(searchFrom);
        const m = re.exec(slice);
        if (!m) break;
        const keyStart = searchFrom + m.index + m[1].length;
        const valueStart = keyStart + key.length + 1;
        markers.push({ key: key.toLowerCase(), valueStart, keyStart });
        searchFrom = valueStart;
        re.lastIndex = 0;
      }
    }

    if (markers.length === 0) {
      const parts = s.split(/[,&]/);
      for (const part of parts) {
        if (!part.includes('=')) continue;
        const eq = part.indexOf('=');
        const k = part.substring(0, eq).trim().toLowerCase();
        const value = part.substring(eq + 1).trim();
        if (k && value) out[k] = value;
      }
      return out;
    }

    markers.sort((a, b) => a.keyStart - b.keyStart);
    for (let i = 0; i < markers.length; i++) {
      const { key, valueStart } = markers[i];
      const valueEnd = i + 1 < markers.length ? markers[i + 1].keyStart : s.length;
      let value = s.slice(valueStart, valueEnd).trim();
      if (value.endsWith(',') || value.endsWith('&')) {
        value = value.slice(0, -1).trim();
      }
      if (value) out[key] = value;
    }
    return out;
  }

  /**
   * @param {string} raw
   * @returns {Record<string, string>}
   */
  function parseProperties(raw) {
    const out = /** @type {Record<string, string>} */ ({});
    const s = decodeParam(raw);
    if (!s) return out;

    const segments = s.split(',');
    for (const segment of segments) {
      const part = segment.trim();
      if (!part.includes('=')) continue;
      const eq = part.indexOf('=');
      const key = part.substring(0, eq).trim();
      const value = part.substring(eq + 1).trim();
      if (key && value) out[key] = value;
    }
    return out;
  }

  /**
   * @param {string} raw
   * @returns {string[]}
   */
  function parseAccessoryVariants(raw) {
    const s = decodeParam(raw);
    if (!s) return [];
    return s
      .split(',')
      .map((p) => p.trim())
      .filter((p) => /^\d+$/.test(p));
  }

  /**
   * @param {string} [search]
   * @returns {boolean}
   */
  function hasConfiguratorParams(search) {
    let params;
    try {
      params = new URLSearchParams(search != null ? search : window.location.search);
    } catch {
      return false;
    }
    for (const key of CONFIGURATOR_PARAM_KEYS) {
      const v = params.get(key);
      if (v != null && String(v).trim() !== '') return true;
    }
    return false;
  }

  /**
   * @param {string} [search]
   * @returns {string}
   */
  function readPriceFromSearch(search) {
    let params;
    try {
      params = new URLSearchParams(search != null ? search : window.location.search);
    } catch {
      return '';
    }
    for (const key of PRICE_PARAM_KEYS) {
      const v = params.get(key);
      if (v) return decodeParam(v);
    }
    const meta = params.get('productMeta') || params.get('product_meta');
    if (meta) {
      const parsed = parseProductMeta(meta);
      if (parsed.price) return parsed.price;
    }
    return '';
  }

  /**
   * @param {string} [search]
   * @returns {{
   *   variantId: string;
   *   productMeta: Record<string, string>;
   *   productMetaRaw: string;
   *   properties: Record<string, string>;
   *   propertiesRaw: string;
   *   price: string;
   *   snapshotUrl: string;
   *   customDressName: string;
   *   accessoryVariants: string[];
   * }}
   */
  function readConfiguratorFromUrl(search) {
    let params;
    try {
      params = new URLSearchParams(search != null ? search : window.location.search);
    } catch {
      params = new URLSearchParams();
    }

    const productMetaRaw = decodeParam(params.get('productMeta') || params.get('product_meta') || '');
    const propertiesRaw = decodeParam(params.get('properties') || '');
    const accessoryRaw = decodeParam(params.get('accessoryVariants') || '');

    return {
      variantId: decodeParam(params.get('variant') || ''),
      productMeta: parseProductMeta(productMetaRaw),
      productMetaRaw,
      properties: parseProperties(propertiesRaw),
      propertiesRaw,
      price: readPriceFromSearch(search),
      snapshotUrl: decodeParam(params.get('snapshotUrl') || ''),
      customDressName: decodeParam(params.get('custom_dress_name') || ''),
      accessoryVariants: parseAccessoryVariants(accessoryRaw),
    };
  }

  /** @param {string} productId */
  function storageKey(productId) {
    return `product_${productId}_url_params`;
  }

  /**
   * @param {string} productId
   * @returns {ReturnType<typeof readConfiguratorFromUrl> | null}
   */
  function readConfiguratorFromStorage(productId) {
    if (!productId) return null;
    try {
      const raw = localStorage.getItem(storageKey(productId));
      if (!raw) return null;
      const data = JSON.parse(raw);

      const productMeta =
        data.productMeta && typeof data.productMeta === 'object' ? data.productMeta : {};
      const properties =
        data.properties && typeof data.properties === 'object' ? data.properties : {};

      return {
        variantId: '',
        productMeta,
        productMetaRaw: data.productMetaRaw || '',
        properties,
        propertiesRaw: data.propertiesRaw || '',
        price: data.customPrice ? String(data.customPrice).trim() : '',
        snapshotUrl: data.snapshotUrl || '',
        customDressName: '',
        accessoryVariants: Array.isArray(data.accessoryVariants)
          ? data.accessoryVariants.map(String)
          : [],
      };
    } catch {
      return null;
    }
  }

  /**
   * @param {string} productId
   * @param {ReturnType<typeof readConfiguratorFromUrl>} config
   */
  function writeConfiguratorToStorage(productId, config) {
    if (!productId || !config) return;
    const stored = {
      productMeta: config.productMeta,
      productMetaRaw: config.productMetaRaw,
      properties: config.properties,
      propertiesRaw: config.propertiesRaw,
      snapshotUrl: config.snapshotUrl,
      customPrice: config.price,
      accessoryVariants: config.accessoryVariants,
      timestamp: new Date().toISOString(),
      productId,
    };
    try {
      localStorage.setItem(storageKey(productId), JSON.stringify(stored));
    } catch {
      /* quota / private mode */
    }
  }

  /**
   * Merge URL + storage; URL wins when present.
   * @param {string} productId
   * @param {string} [search]
   */
  function resolveConfigurator(productId, search) {
    const fromUrl = readConfiguratorFromUrl(search);
    const fromStorage = readConfiguratorFromStorage(productId);

    if (!hasConfiguratorParams(search) && !fromStorage) return null;

    if (!fromStorage) return fromUrl;

    return {
      variantId: fromUrl.variantId || fromStorage.variantId,
      productMeta: Object.keys(fromUrl.productMeta).length ? fromUrl.productMeta : fromStorage.productMeta,
      productMetaRaw: fromUrl.productMetaRaw || fromStorage.productMetaRaw,
      properties: Object.keys(fromUrl.properties).length ? fromUrl.properties : fromStorage.properties,
      propertiesRaw: fromUrl.propertiesRaw || fromStorage.propertiesRaw,
      price: fromUrl.price || fromStorage.price,
      snapshotUrl: fromUrl.snapshotUrl || fromStorage.snapshotUrl,
      customDressName: fromUrl.customDressName || fromStorage.customDressName,
      accessoryVariants: fromUrl.accessoryVariants.length
        ? fromUrl.accessoryVariants
        : fromStorage.accessoryVariants,
    };
  }

  const SHOPIFY_LINE_PROPERTY_MAX = 255;

  window.BridalUrlParams = {
    PRODUCT_META_KEYS,
    PRICE_PARAM_KEYS,
    CONFIGURATOR_PARAM_KEYS,
    SHOPIFY_LINE_PROPERTY_MAX,
    decodeParam,
    parseProductMeta,
    parseProperties,
    parseAccessoryVariants,
    hasConfiguratorParams,
    readPriceFromSearch,
    readConfiguratorFromUrl,
    readConfiguratorFromStorage,
    writeConfiguratorToStorage,
    resolveConfigurator,
    storageKey,
  };
})();
